// Grouped client for User operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { DataFormActionRequest } from '../models/DataFormActionRequest';
import { DataFormActionResponse } from '../models/DataFormActionResponse';
import { StartUserCreateFileRequest } from '../models/StartUserCreateFileRequest';
import { User } from '../models/User';
import { FilterUserRequest } from '../models/FilterUserRequest';
import { DataviewUserResponse } from '../models/DataviewUserResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { DataFormActionRequestSerializer } from '../serializers/DataFormActionRequestSerializer';
import { DataFormActionResponseSerializer } from '../serializers/DataFormActionResponseSerializer';
import { StartUserCreateFileRequestSerializer } from '../serializers/StartUserCreateFileRequestSerializer';
import { UserSerializer } from '../serializers/UserSerializer';
import { FilterUserRequestSerializer } from '../serializers/FilterUserRequestSerializer';
import { DataviewUserResponseSerializer } from '../serializers/DataviewUserResponseSerializer';

/**
 * HTTP client for User operations
 *
 * Client for User operations
 */
export class UserClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  /**
   * Impersonate User
   * ImpersonateUser
   * @param options Optional request options (additional headers, etc.)
   */
  async impersontateUser(id: string, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/User({id})/Impersonate'
      .replace('{id}', encodeURIComponent(String(id)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getUserCreateFileForm(objectId: string, fileName?: string | null, contentType?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/User({objectId})/UserAction(b1579ee4-a94c-44ed-9a9a-805d43592ef2)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (fileName !== undefined) {
      queryParams.append('FileName', String(fileName));
    }
    if (contentType !== undefined) {
      queryParams.append('ContentType', String(contentType));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async runUserCreateFileAction(objectId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/User({objectId})/UserAction(b1579ee4-a94c-44ed-9a9a-805d43592ef2)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async startUserCreateFile(objectId: string, request?: StartUserCreateFileRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/User({objectId})/UserAction(b1579ee4-a94c-44ed-9a9a-805d43592ef2)'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(StartUserCreateFileRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getFormForUser(formName: string, objectId: string, organizationId?: string | null, name?: string | null, userRoleId?: string | null, objectStatusId?: string | null, description?: string | null, id?: string | null, t?: string | null, email?: string | null, phone?: string | null, timeZoneId?: string | null, isActive?: boolean | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/User({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (organizationId !== undefined) {
      queryParams.append('OrganizationId', String(organizationId));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (userRoleId !== undefined) {
      queryParams.append('UserRoleId', String(userRoleId));
    }
    if (objectStatusId !== undefined) {
      queryParams.append('ObjectStatusId', String(objectStatusId));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (t !== undefined) {
      queryParams.append('_t', String(t));
    }
    if (email !== undefined) {
      queryParams.append('Email', String(email));
    }
    if (phone !== undefined) {
      queryParams.append('Phone', String(phone));
    }
    if (timeZoneId !== undefined) {
      queryParams.append('TimeZoneId', String(timeZoneId));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeUser(formName: string, organizationId?: string | null, name?: string | null, userRoleId?: string | null, objectStatusId?: string | null, description?: string | null, id?: string | null, t?: string | null, email?: string | null, phone?: string | null, timeZoneId?: string | null, isActive?: boolean | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/User/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (organizationId !== undefined) {
      queryParams.append('OrganizationId', String(organizationId));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (userRoleId !== undefined) {
      queryParams.append('UserRoleId', String(userRoleId));
    }
    if (objectStatusId !== undefined) {
      queryParams.append('ObjectStatusId', String(objectStatusId));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (t !== undefined) {
      queryParams.append('_t', String(t));
    }
    if (email !== undefined) {
      queryParams.append('Email', String(email));
    }
    if (phone !== undefined) {
      queryParams.append('Phone', String(phone));
    }
    if (timeZoneId !== undefined) {
      queryParams.append('TimeZoneId', String(timeZoneId));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterUser(request?: FilterUserRequest | null, options?: { headers?: Record<string, string> }): Promise<User[]> {
    const path = '/app/api/Object/User/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterUserRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => UserSerializer.fromObject(item));
  }

  async recentUser(request?: FilterUserRequest | null, options?: { headers?: Record<string, string> }): Promise<User[]> {
    const path = '/app/api/Object/User/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterUserRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => UserSerializer.fromObject(item));
  }

  async dataViewUser(request?: FilterUserRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewUserResponse> {
    const path = '/app/api/Object/User/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterUserRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewUserResponseSerializer.fromObject(data);
  }

}
